import { useState, useEffect } from 'react';
import { Deal } from 'views/Deals/types';
import { geocodeAddress } from 'utils/geocoding';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import { useCoordinatesCache } from './useCoordinatesCache';

export const useMapCoordinates = (deal: Pick<Deal, 'longitude' | 'latitude' | 'address' | 'city' | 'state' | 'zipCode'> | null) => {
    const [coordinates, setCoordinates] = useState<{
        lat: number;
        lng: number
    } | null>(null);
    const { getCachedCoordinates, setCachedCoordinates } = useCoordinatesCache();

    useEffect(() => {
        const getCoordinates = async () => {
            if (!deal) {
                setCoordinates(null);
                return;
            }

            // If we already have coordinates, use them
            if (deal.latitude && deal.longitude) {
                setCoordinates({ lat: deal.latitude, lng: deal.longitude });
                return;
            }

            // If we have address details, first check cache then geocode if needed
            if (deal.address || deal.city || deal.state || deal.zipCode) {
                const searchQuery = `${deal.address}, ${deal.city}, ${deal.state} ${deal.zipCode || ''}`.trim();
                const cachedCoordinates = getCachedCoordinates(searchQuery);
                if (cachedCoordinates) {
                    setCoordinates(cachedCoordinates);
                    return;
                }

                const result = await geocodeAddress(searchQuery);
                if (result?.lat && result?.lng) {
                    setCachedCoordinates(searchQuery, result);
                    setCoordinates({
                        lat: result.lat,
                        lng: result.lng,
                    });
                }
            } else {
                setCoordinates(null);
            }
        };

        getCoordinates();
    }, [deal, getCachedCoordinates, setCachedCoordinates]);

    const lng = coordinates?.lng ?? DEFAULT_NYC_COORDINATES.lng;
    const lat = coordinates?.lat ?? DEFAULT_NYC_COORDINATES.lat;

    return { lat, lng, coordinates };
};
